import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Leaf, Calculator } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { CalculatorTransparency, transparencyData } from './CalculatorTransparency';

const CO2Kalkylator = () => {
  const { language } = useLanguage();
  const [virkesvolym, setVirkesvolym] = useState(200);
  const [tradslag, setTradslag] = useState('blandskog');
  const [result, setResult] = useState(null);

  // Biomass expansion factors (BEF) and carbon content
  const speciesFactors = {
    gran: { bef: 1.3, carbonContent: 0.5, density: 400 },
    tall: { bef: 1.25, carbonContent: 0.5, density: 420 },
    bjork: { bef: 1.4, carbonContent: 0.48, density: 510 },
    blandskog: { bef: 1.3, carbonContent: 0.49, density: 430 }
  };

  const speciesNames = {
    gran: 'Gran',
    tall: 'Tall',
    bjork: 'Björk',
    blandskog: 'Blandskog'
  };

  const content = {
    sv: {
      title: 'CO₂ kalkylator för skog',
      description: 'Beräkna kollagring och CO₂-bindning i din skog. Se hur mycket koldioxid som lagras i biomassan och uppskattat värde för kolkrediter.',
      volume: 'Virkesvolym (m³/ha)',
      treeSpecies: 'Trädslag',
      calculate: 'Beräkna kollagring',
      result: 'Resultat för',
      stemBiomass: 'Biomassa (stam)',
      totalBiomass: 'Total biomassa',
      carbonContent: 'Kolinnehåll',
      co2Storage: 'CO₂ lagring',
      annualSequestration: 'Årlig CO₂-bindning (est.)',
      carbonValue: 'Uppskattat kolvärde',
      atPrice: 'Vid 900 kr/ton CO₂'
    },
    de: {
      title: 'CO₂ Rechner für Wald',
      description: 'Berechnen Sie Kohlenstoffspeicherung und CO₂-Bindung in Ihrem Wald.',
      volume: 'Holzvolumen (m³/ha)',
      treeSpecies: 'Baumart',
      calculate: 'Kohlenstoffspeicherung berechnen',
      result: 'Ergebnis für',
      stemBiomass: 'Biomasse (Stamm)',
      totalBiomass: 'Gesamtbiomasse',
      carbonContent: 'Kohlenstoffgehalt',
      co2Storage: 'CO₂ Speicherung',
      annualSequestration: 'Jährliche CO₂-Bindung',
      carbonValue: 'Geschätzter Kohlenstoffwert',
      atPrice: 'Bei 900 SEK/t CO₂'
    },
    en: {
      title: 'CO₂ Forest Calculator',
      description: 'Calculate carbon storage and CO₂ sequestration in your forest.',
      volume: 'Timber volume (m³/ha)',
      treeSpecies: 'Tree species',
      calculate: 'Calculate carbon storage',
      result: 'Result for',
      stemBiomass: 'Stem biomass',
      totalBiomass: 'Total biomass',
      carbonContent: 'Carbon content',
      co2Storage: 'CO₂ storage',
      annualSequestration: 'Annual CO₂ sequestration',
      carbonValue: 'Estimated carbon value',
      atPrice: 'At 900 SEK/t CO₂'
    },
    se: {
      title: 'CO₂ rehkenastin meahccái',
      description: 'Rehkenas čáđačoaggimiid ja CO₂-čatnama du meahccis.',
      volume: 'Muorravoluma (m³/ha)',
      treeSpecies: 'Muorralágat',
      calculate: 'Rehkenas čáđačoaggimiid',
      result: 'Boađus'
    }
  };

  const c = content[language] || content.sv;
  const transparency = transparencyData.co2?.[language] || transparencyData.co2?.sv;

  const calculateCO2 = () => {
    const factors = speciesFactors[tradslag];
    const biomassStam = (virkesvolym * factors.density) / 1000;
    const totalBiomass = biomassStam * factors.bef;
    const carbonContent = totalBiomass * factors.carbonContent;
    const co2Storage = carbonContent * (44 / 12);
    const co2Price = 900;
    const co2Value = co2Storage * co2Price;
    const annualGrowthRate = 0.05;
    const annualSequestration = co2Storage * annualGrowthRate;
    
    setResult({
      biomassStam: biomassStam.toFixed(1),
      totalBiomass: totalBiomass.toFixed(1),
      carbonContent: carbonContent.toFixed(1),
      co2Storage: co2Storage.toFixed(1),
      co2Value: Math.round(co2Value),
      annualSequestration: annualSequestration.toFixed(2)
    });
  };

  // Build assumptions from current values
  const assumptions = result ? [
    { label: 'Trädslag', value: speciesNames[tradslag], unit: '' },
    { label: 'Virkesvolym', value: virkesvolym, unit: 'm³/ha' },
    { label: 'BEF-faktor', value: speciesFactors[tradslag].bef, unit: '' },
    { label: 'Kolinnehåll', value: (speciesFactors[tradslag].carbonContent * 100), unit: '%' },
    { label: 'CO₂-pris', value: 900, unit: 'SEK/ton' }
  ] : [];

  return (
    <Card className="w-full" data-testid="co2-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <Leaf className="h-5 w-5" />
          {c.title}
        </CardTitle>
        <CardDescription>{c.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="volym">{c.volume}</Label>
            <Input
              id="volym"
              type="number"
              value={virkesvolym}
              onChange={(e) => setVirkesvolym(Number(e.target.value))}
              min={1}
              data-testid="volym-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="tradslag">{c.treeSpecies}</Label>
            <Select value={tradslag} onValueChange={setTradslag}>
              <SelectTrigger id="tradslag" data-testid="tradslag-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gran">Gran</SelectItem>
                <SelectItem value="tall">Tall</SelectItem>
                <SelectItem value="bjork">Björk</SelectItem>
                <SelectItem value="blandskog">Blandskog</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button 
          onClick={calculateCO2} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          {c.calculate}
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-3">{c.result} {speciesNames[tradslag]}</h4>
            
            <div className="grid grid-cols-2 gap-4 pb-3 border-b border-forest-200">
              <div>
                <p className="text-sm text-muted-foreground">{c.stemBiomass}</p>
                <p className="text-lg font-semibold text-forest-700">{result.biomassStam} ton/ha</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{c.totalBiomass}</p>
                <p className="text-lg font-semibold text-forest-700">{result.totalBiomass} ton/ha</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 py-3 border-b border-forest-200">
              <div>
                <p className="text-sm text-muted-foreground">{c.carbonContent}</p>
                <p className="text-lg font-semibold text-forest-700">{result.carbonContent} ton C/ha</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{c.co2Storage}</p>
                <p className="text-2xl font-bold text-green-600">{result.co2Storage} ton/ha</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 pt-3">
              <div>
                <p className="text-sm text-muted-foreground">{c.annualSequestration}</p>
                <p className="text-lg font-semibold text-green-600">+{result.annualSequestration} ton/år</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{c.carbonValue}</p>
                <p className="text-xl font-bold text-forest-700">{result.co2Value.toLocaleString('sv-SE')} kr</p>
                <p className="text-xs text-muted-foreground">{c.atPrice}</p>
              </div>
            </div>
          </div>
        )}

        {/* Transparency Section */}
        {result && transparency && (
          <CalculatorTransparency
            assumptions={assumptions}
            howItWorks={transparency.howItWorks}
            sources={transparency.sources}
            didYouKnow={transparency.didYouKnow}
            relatedCalculators={transparency.relatedCalculators}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default CO2Kalkylator;
